import {createFileRoute} from '@tanstack/react-router';
import {makeApiFilterParam, UserStatus} from 'shared';

import {assetKeys} from '@/features/Asset/api/queryKeys';
import {getAsset} from '@/features/Asset/api/readAsset';
import {UpdateAssetForm} from '@/features/Asset/components/UpdateAssetForm';
import {
  categoryKeys,
  categoryPresetKeys,
  layoutTemplateKeys,
  severityScaleKeys,
} from '@/features/Library/api/queryKeys';
import {getCategories} from '@/features/Library/api/readCategories';
import {getCategoryPresets} from '@/features/Library/api/readCategoryPresets';
import {getLayoutTemplates} from '@/features/Library/api/readLayoutTemplates';
import {getSeverityScales} from '@/features/Library/api/readSeverityScales';
import {siteKeys} from '@/features/Site/api/queryKeys';
import {getSites} from '@/features/Site/api/readSites';
import {userKeys} from '@/features/User/api/queryKeys';
import {getUsers} from '@/features/User/api/readUsers';
import {makeDocumentTitle} from '@/utils/makeDocumentTitle';

export const Route = createFileRoute('/_b/_p/assets/update/$assetId')({
  component: UpdateAssetForm,

  beforeLoad: ({params, context}) => {
    makeDocumentTitle(`Edit ${params.assetId}`);

    const userFilter = {
      filter: makeApiFilterParam({
        prop: 'status',
        value: UserStatus.Activated,
        comparison: 'eq',
      }),
    };

    return {
      ...context,
      readAssetQuery: {
        queryKey: assetKeys.detail(params.assetId),
        queryFn: () => getAsset(params.assetId),
      },
      readAssetCategoriesQuery: {
        queryKey: categoryKeys.list({assetId: params.assetId}),
        queryFn: () => getCategories({assetId: params.assetId}),
      },
      readSitesQuery: {
        queryKey: siteKeys.list(),
        queryFn: () => getSites(),
      },
      readCategoriesQuery: {
        queryKey: categoryKeys.list(),
        queryFn: () => getCategories(),
      },
      readCategoryPresetsQuery: {
        queryKey: categoryPresetKeys.list(),
        queryFn: () => getCategoryPresets(),
      },
      readSeveritiesQuery: {
        queryKey: severityScaleKeys.list(),
        queryFn: () => getSeverityScales(),
      },
      readUsersQuery: {
        queryKey: userKeys.list(userFilter),
        queryFn: () => getUsers(userFilter),
      },
      readLayoutTemplatesQuery: {
        queryKey: layoutTemplateKeys.list(),
        queryFn: () => getLayoutTemplates(),
      },
    };
  },

  loader({context}) {
    return Promise.all([
      context.queryClient.ensureQueryData(context.readAssetQuery),
      context.queryClient.ensureQueryData(context.readAssetCategoriesQuery),
      context.queryClient.ensureQueryData(context.readSitesQuery),
      context.queryClient.ensureQueryData(context.readCategoriesQuery),
      context.queryClient.ensureQueryData(context.readCategoryPresetsQuery),
      context.queryClient.ensureQueryData(context.readSeveritiesQuery),
      context.queryClient.ensureQueryData(context.readUsersQuery),
      context.queryClient.ensureQueryData(context.readLayoutTemplatesQuery),
    ]);
  },
});
